/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Fragment } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { removeSlashFromPath } from "@/shared/functions/functions";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import Typography from "@mui/material/Typography";

import TableIsEmptyIcon from "../../../../assets/customIcons/emptyTable/TableIsEmptyIcon";
import { useAuth } from "../../../../context/Auth/AuthProvider";
import { DashboardCardNames } from "../../../../Pages/Dashboard/types";
import { FILES_TABLES } from "../../../../Pages/Files";
import {
  INTEGRATION_TYPE,
  INTEGRATIONS_TABLES
} from "../../../../Pages/Integrations/types";
import { USERS_TABLES } from "../../../../Pages/Users/types";
import { USER_PERMISSIONS } from "../../../../roles/types/enums";
import {
  DRIVERS_ACTIONS,
  ROUTES,
  TABLE_NAMES,
  USERS_ACTIONS,
  VEHICLES_ACTIONS
} from "../../../../shared";
import Button from "../../../Button/Button";
import AddDriverDialog from "../../../PageHeader/actions/dialogs/drivers/AddDriverDialog/AddDriverDialog";
import InviteUserDialog from "../../../PageHeader/actions/dialogs/users/InviteUserDialog/InviteUserDialog";
import AddVehiclesDialog from "../../../PageHeader/actions/dialogs/vehicles/AddVehicleDialog/AddVehiclesDialog";
import { useDialog } from "../../../PageHeader/actions/hooks/useDialog";
import { useTableStyles } from "../../styles/styles";

interface INoInitialResultsProps {
  route: ROUTES;
  dashboardCardName?: DashboardCardNames;
}

const NoInitialResults = ({
  route,
  dashboardCardName
}: INoInitialResultsProps) => {
  const { getAccessStatus } = useAuth();

  const location = useLocation();
  const { t } = useTranslation();
  const { isDialogOpen, openDialog, closeDialog } = useDialog();

  const openAddDialog = useIntegrationsDialogStore(
    state => state.openAddDialog
  );

  const { emptyTableTitle, emptyTableDescription } = useTableStyles();

  const isUserInvitationsTable = location.pathname === ROUTES.UsersInvitations;

  const userPermissionsForTables: { [key: string]: USER_PERMISSIONS } = {
    [ROUTES.Vehicles]: USER_PERMISSIONS.ManageVehicles,
    [ROUTES.Drivers]: USER_PERMISSIONS.ManageDrivers,
    [ROUTES.Integrations]: USER_PERMISSIONS.ManageIntegrations,
    [ROUTES.IntegrationsApi]: USER_PERMISSIONS.ManageApiCredentials,
    [ROUTES.AnalysisProviders]: USER_PERMISSIONS.ManageIntegrations
  };

  const isButtonDisplayed = () => {
    switch (route) {
      case ROUTES.DriverFiles:
      case ROUTES.VehicleFiles:
      case ROUTES.Companies:
      case ROUTES.Dashboard:
        return false;

      case ROUTES.Users:
        return !isUserInvitationsTable;

      default:
        return getAccessStatus(userPermissionsForTables[route]);
    }
  };

  const handleAddItemClick = () => {
    switch (route) {
      case ROUTES.Vehicles:
        return openDialog(VEHICLES_ACTIONS.AddVehicle);
      case ROUTES.Drivers:
        return openDialog(DRIVERS_ACTIONS.AddDriver);
      case ROUTES.Users:
        return openDialog(USERS_ACTIONS.InviteUser);
      case ROUTES.Integrations:
        return openAddDialog(INTEGRATION_TYPE.Sftp);
      case ROUTES.IntegrationsApi:
        return openAddDialog(INTEGRATION_TYPE.Api);
      case ROUTES.AnalysisProviders:
        return openAddDialog(INTEGRATION_TYPE.AnalysisProvider);

      default:
        return;
    }
  };

  const emptyTableName = () => {
    switch (route) {
      case ROUTES.VehicleFiles:
        return FILES_TABLES.VehicleFiles.toLowerCase();

      case ROUTES.DriverFiles:
        return FILES_TABLES.DriverFiles.toLowerCase();

      case ROUTES.UsersInvitations:
        return USERS_TABLES.UsersInvitations.toLowerCase();

      case ROUTES.IntegrationsApi:
        return INTEGRATIONS_TABLES.IntegrationsApi.toLowerCase();

      case ROUTES.AnalysisProviders:
        return INTEGRATIONS_TABLES.AnalysisProviders.toLowerCase();

      case ROUTES.Dashboard:
        return dashboardCardName?.toLowerCase();

      default:
        return removeSlashFromPath(route);
    }
  };

  return (
    <Fragment>
      <TableIsEmptyIcon
        css={css({
          fontSize: "120px",
          padding: "32px 0 16px 0"
        })}
      />

      <Typography
        variant="body1Medium"
        css={css(emptyTableTitle)}
        component="span"
      >
        {t(
          `Table##empty table##no initial results##title##${emptyTableName()}`
        )}
      </Typography>
      <Typography
        variant="body2Medium"
        css={css(emptyTableDescription)}
        component="span"
      >
        {t(
          `Table##empty table##no initial results##description##${emptyTableName()}`
        )}
      </Typography>

      {isButtonDisplayed() && (
        <>
          <Button
            variant="textOnly"
            color="primary"
            size="small"
            onClick={handleAddItemClick}
          >
            {t(`Table##empty table##button##add##${emptyTableName()}`)}
          </Button>
          <AddVehiclesDialog
            isOpen={isDialogOpen[VEHICLES_ACTIONS.AddVehicle]}
            onClose={closeDialog}
          />
          <AddDriverDialog
            isOpen={isDialogOpen[DRIVERS_ACTIONS.AddDriver]}
            onClose={closeDialog}
          />
          <InviteUserDialog
            tableName={TABLE_NAMES.Users}
            isOpen={isDialogOpen[USERS_ACTIONS.InviteUser]}
            onClose={closeDialog}
          />
        </>
      )}
    </Fragment>
  );
};

export default NoInitialResults;
